import { useEffect, useState } from "react";

import { useUpdatePreferences } from "@/hooks/useUpdatePreferences";
import { createPageOverride } from "@/lib/page-override";
import { readBrowserPreferences, resolvePreferences } from "@/lib/preferences";
import { useOptionalAuthSession } from "@/lib/session-context";

const STORAGE_KEY = "maxwell.surface";
const EVENT_NAME = "app:maxwell-surface";

/** Default rail width — wider than the legacy 360px fixed rail (spec §1.A). */
export const DEFAULT_W = 420;
/** Narrowest the rail can be dragged. */
export const MIN_W = 360;
/** Widest the rail can be dragged before the user should pop out instead. */
export const MAX_W = 720;

export interface MaxwellSurface {
  open: boolean;
  width: number;
  setOpen: (open: boolean) => void;
  setWidth: (width: number) => void;
  MIN_W: number;
  MAX_W: number;
}

/** A read-only session's open/closed, shared by the rail and the workspace. */
const useReadOnlyOpen = createPageOverride<boolean>();

/** Clamp a candidate width into the draggable range. */
export function clampWidth(width: number): number {
  return Math.min(MAX_W, Math.max(MIN_W, Math.round(width)));
}

/**
 * The Maxwell rail's surface: whether it is open, and how wide.
 *
 * **Open is a server-side preference** (`session.preferences.show_maxwell_panel`,
 * Preferences → Modules), so closing the rail closes it on every browser.
 * The rail's own close button, the topbar entry and the Preferences switch
 * all write it, and the optimistic session update moves every consumer at
 * once. A read-only session (a super admin viewing as someone) refuses
 * writes, so there open/close lasts only for the page, held in one store
 * every consumer reads (`createPageOverride`), so the workspace's "Dock to
 * rail" opens the rail.
 *
 * **Width stays in this browser** (`localStorage`): it fits a screen, not a
 * person. Same-tab updates fan out via a custom event so the rail and its
 * drag handle re-render together; cross-tab updates ride the native `storage`
 * event. Always read back clamped to `[MIN_W, MAX_W]`, so a stale or
 * hand-edited value can never paint the rail off-range.
 */
export function useMaxwellSurface(): MaxwellSurface {
  const session = useOptionalAuthSession();
  const update = useUpdatePreferences();
  const [width, setWidthState] = useState<number>(() => readWidth());
  const [localOpen, setLocalOpen] = useReadOnlyOpen(session?.user_id);

  useEffect(() => {
    const onCustom = (e: Event) => setWidthState((e as CustomEvent<number>).detail);
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setWidthState(readWidth());
    };
    window.addEventListener(EVENT_NAME, onCustom as EventListener);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onCustom as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const saved = session
    ? resolvePreferences(session, readBrowserPreferences(session.user_id)).show_maxwell_panel
    : true;
  const readOnly = !session || session.impersonating;

  const setWidth = (next: number) => {
    const clamped = clampWidth(next);
    setWidthState(clamped);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ width: clamped }));
    } catch {
      // localStorage may throw in private mode / disabled storage — the
      // in-memory state still works for the current page.
    }
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: clamped }));
  };

  return {
    open: readOnly ? (localOpen ?? saved) : saved,
    width,
    setOpen: (open) => (readOnly ? setLocalOpen(open) : update.mutate({ show_maxwell_panel: open })),
    setWidth,
    MIN_W,
    MAX_W,
  };
}

function readWidth(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_W;
    const width = (JSON.parse(raw) as { width?: unknown }).width;
    return typeof width === "number" ? clampWidth(width) : DEFAULT_W;
  } catch {
    return DEFAULT_W;
  }
}
